import { catchError, delay as delayOperator, map, retryWhen, scan } from 'rxjs/operators';
/**
 * Throws the error built by `errorFn` if `conditionFn` evaluates to be truthy.
 *
 * @param conditionFn Determines if an error should be thrown.
 * @param errorFn Evaluates to the error which can be thrown.
 *
 * @example
 * ```
 * const input = cold('   ft', { f: false, t: true });
 * const expected = cold('f#', { f: false }, 'error');
 *
 * const result = input.pipe(
 *   // will throw 'error' for values === true
 *   throwIf(
 *     (val) => val
 *     () => error
 *   )
 * );
 * ```
 */
export function throwIf(conditionFn, errorFn) {
  return (input) => input.pipe(map((value, index) => {
    if (conditionFn(value, index)) {
      throw errorFn(value, index);
    }
    return value;
  }));
}
/**
 * Catches an error if [conditionFn] evaluates to be truthy.
 * If the error is catched, the original observable sequence will be replaced with the result of [selectorFn].
 *
 * @param conditionFn Determines if an error should be catched.
 * @param selectorFn Selector which provides a new observable.
 *                   The newly provided observable will continue the observable chain.
 *
 * @example
 * ```
 * // Example with condition evaluating to `true`:
 * const source =      cold('v#');
 * const replacement = cold('r|');
 *
 * const expected =    cold('vr|');
 *
 * const result = source.pipe(
 *   catchIf(
 *     () => true,
 *     () => replacement
 *   )
 * );
 *
 * // Example with condition evaluating to `false`:
 * const source =      cold('v#');
 * const replacement = cold('r|');
 *
 * const expected =    cold('v#');
 *
 * const result = source.pipe(
 *   catchIf(
 *     () => false,
 *     () => replacement
 *   )
 * );
 *
 * ```
 */
export function catchIf(conditionFn, selectorFn) {
  return (input) => input.pipe(catchError((error) => {
    if (conditionFn(error)) {
      return selectorFn(error);
    }
    throw error;
  }));
}
/**
 * Retries an Observable with a [delay].
 * Will retry [count] times. Defaults to `1`.
 *
 * @param delay The delay (milliseconds) the operator will wait before each retry.
 *              This also includes the first try.
 * @param count The number of times the operator will retry the execution.
 *              Defaults to `1`.
 */
export function retryWithDelay(delay, count = 1) {
  return (input) => input.pipe(retryWhen((errors) => errors.pipe(scan((acc, error) => ({ count: acc.count + 1, error }), {
    count: 0,
    error: undefined,
  }), throwIf((current) => current.count > count, (current) => current.error), delayOperator(delay))));
}
//# sourceMappingURL=operators.util.js.map
